import React, { useRef, useLayoutEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Printer, Copy, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fontCss, DEFAULT_FONT, DEFAULT_COLOR, bodyFontCss, DEFAULT_BODY_TYPO } from "@/lib/briefkopfStyles";
import FitA4 from "@/components/FitA4";

const TYP_LABEL = {
  anspruchsschreiben: "Anspruchsschreiben",
  unfallhergang: "Unfallhergang",
  klageschrift: "Klageschrift",
  owi_einspruch: "Einspruch gegen Bußgeldbescheid",
  bezifferungsschreiben: "Bezifferungsschreiben",
  rvg_honorar: "Honorarrechnung nach RVG",
  vollmacht: "Vollmacht",
};

// Highlight important data in paragraph text.
// Returns an array of React nodes with <mark>-style spans.
const HL_PATTERNS = [
  // EUR amounts: "13.082,43 EUR", "1.500 EUR", "€ 1.500,00"
  { key: "money", re: /(\d{1,3}(?:\.\d{3})*(?:,\d{1,2})?\s*(?:EUR|€)|€\s*\d{1,3}(?:\.\d{3})*(?:,\d{1,2})?)/g },
  // § paragraph refs: "§ 249 Abs. 2 BGB", "§§ 7, 18 StVG", "§ 115 VVG"
  { key: "law", re: /(§§?\s*\d+[a-z]?(?:\s*(?:Abs\.|Satz|Nr\.|Alt\.)\s*\d+)*(?:\s+(?:i\.?V\.?m\.?|i\.?S\.?d\.?))?(?:\s+\d+[a-z]?)*\s+[A-ZÄÖÜ][A-Za-zÄÖÜ]{1,6})/g },
  // Dates: "15. April 2026"
  { key: "date", re: /(\d{1,2}\.\s?(?:Januar|Februar|März|April|Mai|Juni|Juli|August|September|Oktober|November|Dezember)\s+\d{4})/g },
  // Deadlines: "14 Tage", "30 Tagen", "2 Wochen"
  { key: "deadline", re: /(\d+\s+(?:Tage[n]?|Wochen|Monate[n]?))/g },
  // Car plates: "GM-TR 6868", "B-AB 1234"
  { key: "plate", re: /\b([A-ZÄÖÜ]{1,3}-[A-ZÄÖÜ]{1,2}\s?\d{1,4}[A-Z]?)\b/g },
  // Long reference numbers (Schadennummern, Aktenzeichen behörde)
  { key: "refnum", re: /\b(\d{10,})\b/g },
  // Percent
  { key: "percent", re: /(\d+(?:,\d+)?\s*(?:%|Prozent))/g },
];

function highlightText(text) {
  if (!text) return text;
  // Collect all matches with positions
  const matches = [];
  HL_PATTERNS.forEach(({ key, re }) => {
    let m;
    re.lastIndex = 0;
    while ((m = re.exec(text)) !== null) {
      matches.push({ start: m.index, end: m.index + m[0].length, text: m[0], key });
    }
  });
  if (matches.length === 0) return text;
  // Sort by start, resolve overlaps (keep first/longest)
  matches.sort((a, b) => a.start - b.start || b.end - a.end);
  const resolved = [];
  let lastEnd = -1;
  for (const m of matches) {
    if (m.start >= lastEnd) {
      resolved.push(m);
      lastEnd = m.end;
    }
  }
  // Build react nodes
  const out = [];
  let pos = 0;
  resolved.forEach((m, i) => {
    if (m.start > pos) out.push(text.slice(pos, m.start));
    out.push(<mark key={`hl-${i}`} className={`hl hl-${m.key}`}>{m.text}</mark>);
    pos = m.end;
  });
  if (pos < text.length) out.push(text.slice(pos));
  return out;
}

// A4 @ 96dpi = 794 × 1123 px
// Margins: 25mm top/bottom + 20mm left/right  = 94.5px top/bot, 75.6px sides
// Usable body height: 1123 - 2*94.5 ≈ 934px
// First page reserves ~270px for letterhead + recipient + meta + betreff
// Continuation pages reserve ~40px for mini header
const FIRST_PAGE_BODY_MAX = 620;
const CONT_PAGE_BODY_MAX = 880;

export default function A4Sheet({ content, user, typ, createdAt, meta = {}, actions = null }) {
  const [copied, setCopied] = useState(false);
  const measureRef = useRef(null);
  const [pages, setPages] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  useLayoutEffect(() => {
    let active = true; let url;
    (async () => {
      if (!user?.logo_path) { setLogoUrl(null); return; }
      try {
        const mod = await import("@/lib/api");
        const r = await mod.default.get("/auth/logo", { responseType: "blob" });
        if (!active) return;
        url = URL.createObjectURL(r.data);
        setLogoUrl(url);
      } catch { setLogoUrl(null); }
    })();
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [user?.logo_path]);

  const today = new Date(createdAt || Date.now()).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  // Clean residual markdown
  const cleanText = (content || "")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/^---+\s*$/gm, "")
    .replace(/^\s*[-*]\s+/gm, "• ")
    .replace(/\n{3,}/g, "\n\n");

  const paragraphs = cleanText.split(/\n\s*\n+/).map(p => p.trim()).filter(Boolean);

  // Measure and paginate after first render
  useLayoutEffect(() => {
    if (!measureRef.current) return;
    const nodes = Array.from(measureRef.current.children);
    if (nodes.length === 0) { setPages([[]]); return; }

    // Detect which paragraphs are headings (so we can keep them with their content)
    const isHeading = (i) => {
      const p = paragraphs[i] || "";
      return /^(I{1,3}|IV|V|VI{0,3}|IX|X)\.\s+[A-ZÄÖÜ]/.test(p) && p.length < 90;
    };

    // Measure heights once
    const heights = nodes.map(n => {
      const r = n.getBoundingClientRect();
      const mb = parseFloat(getComputedStyle(n).marginBottom || "0");
      return r.height + mb;
    });

    const result = [];
    let current = [];
    let used = 0;
    let isFirst = true;

    const pushPage = () => {
      if (current.length > 0) {
        result.push(current);
        current = [];
        used = 0;
        isFirst = false;
      }
    };

    for (let i = 0; i < nodes.length; i++) {
      const h = heights[i];
      const limit = isFirst ? FIRST_PAGE_BODY_MAX : CONT_PAGE_BODY_MAX;

      // Keep heading together with at least one following paragraph
      let requiredHeight = h;
      if (isHeading(i) && i + 1 < nodes.length) {
        requiredHeight += heights[i + 1];
      }

      if (used + requiredHeight > limit && current.length > 0) {
        pushPage();
      }

      current.push(i);
      used += h;
    }
    pushPage();
    if (result.length === 0) result.push([]);
    setPages(result);
  }, [content]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content || "");
    setCopied(true);
    toast.success("In Zwischenablage kopiert");
    setTimeout(() => setCopied(false), 1800);
  };

  const handlePrint = () => {
    const printEl = document.getElementById(`a4-print-${typ}-${createdAt}`);
    if (!printEl) return;
    const html = printEl.outerHTML;
    const w = window.open("", "_blank", "width=900,height=1200");
    if (!w) return;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"/><title>${TYP_LABEL[typ] || "Schriftsatz"}</title>
    <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap" rel="stylesheet">
    <style>
      * { box-sizing: border-box; }
      @page { size: A4; margin: 0; }
      html, body { margin: 0; padding: 0; background: white; }
      body { font-family: 'Cormorant Garamond', 'Times New Roman', serif; color: #0A192F; }
      .a4-print-root { display: block; }
      .a4-page { width: 210mm; min-height: 297mm; padding: 25mm 20mm; page-break-after: always; background: white; position: relative; }
      .a4-page:last-child { page-break-after: auto; }
      .ltr-head { display: grid; grid-template-columns: 1fr auto; align-items: flex-start; border-bottom: 1px solid #111; padding-bottom: 6mm; margin-bottom: 8mm; font-family: 'IBM Plex Sans', sans-serif; gap: 10mm; }
      .ltr-left { padding-top: 18mm; min-width: 0; }
      .ltr-sender-line { font-size: 7pt; color: #4B5563; text-decoration: underline; text-underline-offset: 2px; }
      .ltr-right { text-align: right; min-width: 65mm; font-size: 9pt; color: #4B5563; line-height: 1.4; }
      .ltr-logo { max-width: 55mm; max-height: 22mm; object-fit: contain; margin-left: auto; margin-bottom: 3mm; display: block; }
      .ltr-kanzlei { font-weight: 600; font-size: 13pt; color: #0A192F; }
      .ltr-anwalt { font-size: 9pt; color: #0A192F; margin-top: 2mm; }
      .ltr-titel { font-size: 8pt; color: #4B5563; font-style: italic; }
      .ltr-byline { font-size: 9pt; color: #4B5563; margin-top: 1mm; }
      .ltr-minihdr { font-family: 'IBM Plex Sans', sans-serif; font-size: 8pt; color: #9CA3AF; display: flex; justify-content: space-between; margin-bottom: 8mm; padding-bottom: 3mm; border-bottom: 1px solid #F3F4F6; }
      .rec-block { display: grid; grid-template-columns: 1fr auto; gap: 10mm; margin-bottom: 10mm; font-family: 'IBM Plex Sans', sans-serif; }
      .rec-addr { font-size: 10pt; white-space: pre-line; line-height: 1.5; }
      .rec-right { text-align: right; font-size: 9pt; color: #4B5563; line-height: 1.6; white-space: nowrap; }
      .rec-right strong { color: #0A192F; }
      .rec-date { font-weight: 500; }
      .rec-az { font-family: 'IBM Plex Mono', monospace; font-size: 8.5pt; }
      .meta-row { display: none; }
      article { text-align: justify; hyphens: auto; font-size: 12pt; line-height: 1.6; }
      article p { margin: 0 0 4mm 0; white-space: pre-wrap; }
      article h3 { font-family: 'Cormorant Garamond', serif; font-size: 14pt; font-weight: 600; margin: 6mm 0 3mm 0; letter-spacing: -0.01em; }
      .betreff { font-family: 'IBM Plex Sans', sans-serif; font-weight: 600; font-size: 11pt; margin: 0 0 8mm 0; color: #0A192F; }
      .ltr-foot { position: absolute; bottom: 12mm; left: 20mm; right: 20mm; padding-top: 4mm; border-top: 1px solid #E5E7EB; font-family: 'IBM Plex Sans', sans-serif; font-size: 7.5pt; color: #6B7280; display: grid; grid-template-columns: 1fr auto; gap: 8mm; line-height: 1.5; }
      .ltr-foot .foot-right { text-align: right; }
      mark.hl { background: transparent; color: inherit; padding: 0 1px; border-radius: 2px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      mark.hl-money { background: rgba(211, 47, 47, 0.08); color: #7A1F1F; font-weight: 500; padding: 0 3px; }
      mark.hl-date { color: #0A192F; font-weight: 500; }
      mark.hl-law { font-style: italic; color: #1A2238; font-weight: 500; }
      mark.hl-deadline { background: rgba(245, 158, 11, 0.14); color: #78350F; font-weight: 500; padding: 0 3px; }
      mark.hl-plate { font-family: 'IBM Plex Mono', monospace; font-size: 0.92em; background: rgba(26, 34, 56, 0.05); padding: 0 3px; letter-spacing: 0.02em; }
      mark.hl-refnum { font-family: 'IBM Plex Mono', monospace; font-size: 0.88em; color: #4B5563; }
      mark.hl-percent { color: #1A2238; font-weight: 500; }
    </style></head><body>${html}</body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 500);
  };

  const kanzleiName = user?.kanzlei_name || "Kanzlei";
  const anschriftLine = [user?.kanzlei_adresse, [user?.kanzlei_plz, user?.kanzlei_ort].filter(Boolean).join(" ")]
    .filter(Boolean).join(" · ");
  const contactLine = [user?.kanzlei_telefon, user?.kanzlei_email, user?.kanzlei_website]
    .filter(Boolean).join(" · ");

  // Custom briefkopf / fusszeile from user profile (new shape: {logo_size, left, center, right})
  // with backward-compat for legacy {logo_position, blocks: [...]} and legacy fusszeile array.
  const migrateHeader = (bk) => {
    if (!bk) return null;
    if (bk.left || bk.center || bk.right) return bk;
    const next = { logo_size: bk.logo_size || "medium",
      left: { type: "empty", content: "" },
      center: { type: "empty", content: "" },
      right: { type: "empty", content: "" } };
    if (["left", "center", "right"].includes(bk.logo_position)) {
      next[bk.logo_position] = { type: "logo", content: "" };
    }
    for (const b of bk.blocks || []) {
      if (!["left", "center", "right"].includes(b.position)) continue;
      if (next[b.position].type === "empty") {
        next[b.position] = { type: "text", content: b.content || "" };
      }
    }
    return next;
  };
  const migrateFooter = (fz) => {
    if (!fz) return null;
    if (!Array.isArray(fz)) return fz;
    const next = { left: { type: "empty", content: "" }, center: { type: "empty", content: "" }, right: { type: "empty", content: "" } };
    for (const b of fz) {
      if (!["left", "center", "right"].includes(b.position)) continue;
      if (next[b.position].type === "empty") {
        next[b.position] = { type: "text", content: b.content || "" };
      }
    }
    return next;
  };
  const briefkopf = migrateHeader(user?.briefkopf);
  const fusszeile = migrateFooter(user?.fusszeile);
  // Seitenkopf migration: legacy {type, content} → {blocks: [{...}, empty, empty]}
  const migrateSide = (sk) => {
    if (!sk) return null;
    if (Array.isArray(sk.blocks)) {
      const arr = [...sk.blocks];
      while (arr.length < 3) arr.push({ type: "empty", content: "" });
      return { blocks: arr.slice(0, 3) };
    }
    if (sk.type) {
      return { blocks: [{ ...sk }, { type: "empty", content: "" }, { type: "empty", content: "" }] };
    }
    return null;
  };
  const seitenkopf = migrateSide(user?.seitenkopf);
  const linien = {
    briefkopf_unten:    user?.linien?.briefkopf_unten ?? true,
    fusszeile_oben:     user?.linien?.fusszeile_oben ?? true,
    seitenkopf_links:   user?.linien?.seitenkopf_links ?? true,
    seitenkopf_blocks:  user?.linien?.seitenkopf_blocks ?? true,
  };
  const bodyTypo = { ...DEFAULT_BODY_TYPO, ...(user?.body_typografie || {}) };
  const bodyArticleStyle = {
    fontFamily: bodyFontCss(bodyTypo.font_family),
    fontSize: `${bodyTypo.font_size}pt`,
    lineHeight: bodyTypo.line_height,
  };
  const cellHas = (c) => c && c.type && c.type !== "empty" && (c.type === "logo" || (c.content && c.content.trim()));
  const hasCustomHeader = !!(briefkopf && (cellHas(briefkopf.left) || cellHas(briefkopf.center) || cellHas(briefkopf.right)));
  const hasCustomFooter = !!(fusszeile && (cellHas(fusszeile.left) || cellHas(fusszeile.center) || cellHas(fusszeile.right)));
  const sideActiveBlocks = (seitenkopf?.blocks || []).filter(cellHas);
  const hasSeitenkopf = sideActiveBlocks.length > 0;
  const seitenkopfWidth = seitenkopf?.width ?? 28;
  const seitenkopfPadRight = `${seitenkopfWidth + 16}mm`;

  const LOGO_HEIGHT = { small: "14mm", medium: "22mm", large: "32mm" };
  const logoH = LOGO_HEIGHT[briefkopf?.logo_size] || "22mm";

  // Render a single paragraph (used both in measure and in page)
  const renderPara = (p, idx) => {
    if (/^betreff\b/i.test(p) && idx < 3) {
      return <p key={`p-${idx}`} className="betreff">{highlightText(p)}</p>;
    }
    if (/^(I{1,3}|IV|V|VI{0,3}|IX|X)\.\s+[A-ZÄÖÜ]/.test(p) && p.length < 90) {
      return <h3 key={`p-${idx}`}>{p}</h3>;
    }
    // Markdown-Tabelle (z. B. Kostenaufstellung in der RVG-Rechnung)
    const lines = p.split("\n").map(l => l.trim());
    if (lines.length >= 2 && lines.every(l => l.startsWith("|"))) {
      const rows = lines
        .filter(l => !/^\|\s*:?-{2,}/.test(l))
        .map(l => l.replace(/^\||\|$/g, "").split("|").map(c => c.trim()));
      const [head, ...body] = rows;
      return (
        <table key={`p-${idx}`} className="a4-table">
          {head && <thead><tr>{head.map((c, i) => <th key={i}>{highlightText(c)}</th>)}</tr></thead>}
          <tbody>
            {body.map((r, ri) => (
              <tr key={ri} className={/gesamt|summe/i.test(r[0] || "") ? "a4-table-total" : undefined}>
                {r.map((c, ci) => <td key={ci} className={ci === r.length - 1 ? "num" : undefined}>{highlightText(c)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    return <p key={`p-${idx}`}>{highlightText(p)}</p>;
  };

  const LetterHeadCustom = () => {
    const LEGACY_LOGO_MM = { small: 14, medium: 22, large: 32 };
    const fallbackH = LEGACY_LOGO_MM[briefkopf?.logo_size] ?? 22;
    const cellTextStyle = (cell, base) => ({
      ...base,
      fontFamily: fontCss(cell?.font_family || DEFAULT_FONT),
      color: cell?.font_color || base.color || DEFAULT_COLOR,
    });
    const renderCell = (cell, align) => {
      if (cell?.type === "logo" && logoUrl) {
        const justify = align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start";
        return (
          <div style={{ display: "flex", justifyContent: justify, overflow: "visible" }}>
            <img src={logoUrl} alt="Logo" style={{
              height: `${cell.logo_height ?? fallbackH}mm`,
              width: cell.logo_width ? `${cell.logo_width}mm` : "auto",
              marginLeft: `${cell.logo_offset_x ?? 0}mm`,
              marginTop: `${cell.logo_offset_y ?? 0}mm`,
              maxWidth: "none",
              objectFit: "contain",
              flexShrink: 0,
            }} />
          </div>
        );
      }
      if (cell?.type === "text" && cell.content) {
        return (
          <div style={cellTextStyle(cell, { textAlign: align, fontSize: "9pt", color: "#0A192F", whiteSpace: "pre-line", lineHeight: 1.4 })}>
            {cell.content}
          </div>
        );
      }
      return <div />;
    };
    return (
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: "8mm",
        alignItems: "flex-start",
        borderBottom: linien.briefkopf_unten ? "1px solid #111" : "none",
        paddingBottom: linien.briefkopf_unten ? "6mm" : 0,
        marginBottom: "8mm",
      }}>
        {renderCell(briefkopf.left, "left")}
        {renderCell(briefkopf.center, "center")}
        {renderCell(briefkopf.right, "right")}
      </div>
    );
  };

  const LetterHead = () => (
    <div className="ltr-head">
      <div className="ltr-left">
        {user?.kanzlei_adresse && (
          <div className="ltr-sender-line">
            {kanzleiName} · {user.kanzlei_adresse} · {[user.kanzlei_plz, user.kanzlei_ort].filter(Boolean).join(" ")}
          </div>
        )}
      </div>
      <div className="ltr-right">
        {logoUrl && <img src={logoUrl} alt="Logo" className="ltr-logo" />}
        <div className="ltr-kanzlei">{kanzleiName}</div>
        {user?.full_name && <div className="ltr-anwalt">{user.full_name}</div>}
        {user?.anwalt_titel && <div className="ltr-titel">{user.anwalt_titel}</div>}
      </div>
    </div>
  );

  const MiniHeader = ({ pageNum, totalPages }) => (
    <div className="ltr-minihdr">
      <span>{kanzleiName} · {meta.aktenzeichen || ""}</span>
      <span>Seite {pageNum} von {totalPages}</span>
    </div>
  );

  const RecipientBlock = () => (
    <div className="rec-block">
      <div className="rec-addr">
        {meta.empfaenger || (
          <span style={{ color: "#9CA3AF", fontStyle: "italic" }}>
            [Empfängeradresse — wird aus Akte übernommen]
          </span>
        )}
      </div>
      <div className="rec-right">
        <div className="rec-date">{[user?.kanzlei_ort, today].filter(Boolean).join(", den ")}</div>
        {meta.aktenzeichen && <div className="rec-az">Unser Zeichen: <strong>{meta.aktenzeichen}</strong></div>}
        {meta.schadennr && <div className="rec-az">Schaden-Nr.: <strong>{meta.schadennr}</strong></div>}
      </div>
    </div>
  );

  const MetaRow = () => null;

  const FooterCustom = () => {
    const renderCell = (cell, align) => {
      if (cell?.type === "text" && cell.content) {
        return (
          <div style={{
            textAlign: align,
            whiteSpace: "pre-line",
            lineHeight: 1.5,
            fontSize: "7.5pt",
            fontFamily: fontCss(cell?.font_family || DEFAULT_FONT),
            color: cell?.font_color || "#6B7280",
          }}>
            {cell.content}
          </div>
        );
      }
      return <div />;
    };
    return (
      <div style={{
        position: "absolute",
        bottom: "12mm",
        left: "20mm",
        right: hasSeitenkopf ? seitenkopfPadRight : "20mm",
        paddingTop: linien.fusszeile_oben ? "4mm" : 0,
        borderTop: linien.fusszeile_oben ? "1px solid #E5E7EB" : "none",
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: "8mm",
        fontFamily: "'IBM Plex Sans', sans-serif",
      }}>
        {renderCell(fusszeile.left, "left")}
        {renderCell(fusszeile.center, "center")}
        {renderCell(fusszeile.right, "right")}
      </div>
    );
  };

  const Footer = () => {
    const lines = [];
    if (user?.kanzlei_kammer) lines.push(`Mitglied der ${user.kanzlei_kammer}`);
    if (user?.kanzlei_ustid) lines.push(`USt-IdNr.: ${user.kanzlei_ustid}`);
    if (user?.kanzlei_gerichtsstand) lines.push(`Gerichtsstand: ${user.kanzlei_gerichtsstand}`);
    const inlineStyle = {};
    if (hasSeitenkopf) inlineStyle.right = seitenkopfPadRight;
    if (!linien.fusszeile_oben) { inlineStyle.borderTop = "none"; inlineStyle.paddingTop = 0; }
    return (
      <div className="ltr-foot" style={Object.keys(inlineStyle).length ? inlineStyle : undefined}>
        <div className="foot-left">
          {user?.kanzlei_bank && <div>{user.kanzlei_bank}</div>}
          {user?.kanzlei_iban && <div>Geschäftskonto IBAN: {user.kanzlei_iban}</div>}
          {user?.kanzlei_anderkonto_iban && <div>Anderkonto IBAN: {user.kanzlei_anderkonto_iban}</div>}
          {(user?.kanzlei_bic || user?.kanzlei_anderkonto_bic) && <div>BIC: {user.kanzlei_bic || user.kanzlei_anderkonto_bic}</div>}
        </div>
        <div className="foot-right">
          {lines.map((l, i) => <div key={i}>{l}</div>)}
          <div>{TYP_LABEL[typ] || "Schriftsatz"} · {today}</div>
        </div>
      </div>
    );
  };

  const Seitenkopf = () => {
    if (!hasSeitenkopf) return null;
    const renderBlock = (b, i) => {
      if (b.type === "logo" && logoUrl) {
        return (
          <div key={i} style={{ marginBottom: "3mm", overflow: "hidden" }}>
            <img src={logoUrl} alt="Logo" style={{
              height: `${b.logo_height ?? 16}mm`,
              width: b.logo_width ? `${b.logo_width}mm` : "auto",
              marginLeft: `${b.logo_offset_x ?? 0}mm`,
              marginTop: `${b.logo_offset_y ?? 0}mm`,
              maxWidth: "100%",
              objectFit: "contain",
            }} />
          </div>
        );
      }
      if (b.type === "text" && (b.content || "").trim()) {
        return (
          <div key={i} style={{
            fontSize: "7pt",
            lineHeight: 1.5,
            whiteSpace: "pre-line",
            fontFamily: fontCss(b?.font_family || DEFAULT_FONT),
            color: b?.font_color || "#4B5563",
            paddingTop: i > 0 ? "2.5mm" : 0,
            marginTop: i > 0 ? "2.5mm" : 0,
            borderTop: (i > 0 && linien.seitenkopf_blocks) ? "1px solid #E5E7EB" : "none",
          }}>
            {b.content}
          </div>
        );
      }
      return null;
    };
    return (
      <div style={{
        position: "absolute",
        top: `${25 + (seitenkopf?.offset_y ?? 0)}mm`,
        right: "12mm",
        width: `${seitenkopfWidth}mm`,
        paddingLeft: linien.seitenkopf_links ? "3mm" : 0,
        borderLeft: linien.seitenkopf_links ? "1px solid #1A2238" : "none",
      }}>
        {(seitenkopf.blocks || []).map((b, i) => renderBlock(b, i))}
      </div>
    );
  };

  const totalPages = pages?.length || 1;

  return (
    <div className="space-y-4">
      <style>{sheetStyles}</style>

      <div className="flex items-center gap-2 justify-end flex-wrap">
        {actions}
        <Button variant="outline" size="sm" onClick={handleCopy} data-testid="schrift-copy-btn">
          {copied ? <CheckCircle size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
          {copied ? "Kopiert" : "Kopieren"}
        </Button>
        <Button variant="outline" size="sm" onClick={handlePrint} data-testid="schrift-print-btn">
          <Printer size={14} className="mr-1" /> Drucken / PDF
        </Button>
      </div>

      {/* Hidden measurement container — same width and typography as real article */}
      <div ref={measureRef} className="a4-measure" aria-hidden="true" style={hasSeitenkopf ? { width: `${190 - seitenkopfWidth - 16}mm`, ...bodyArticleStyle } : bodyArticleStyle}>
        {paragraphs.map((p, i) => renderPara(p, i))}
      </div>

      <div className="bg-[#E5E7EB] rounded-lg p-3 md:p-10 overflow-hidden">
        <FitA4>
          <div id={`a4-print-${typ}-${createdAt}`} className="a4-print-root flex flex-col items-center gap-6">
            {(pages || [paragraphs.map((_, i) => i)]).map((indices, pageIdx) => {
              const isFirst = pageIdx === 0;
              const isLast = pageIdx === totalPages - 1;
              return (
                <div key={pageIdx} className="a4-page" style={hasSeitenkopf ? { paddingRight: seitenkopfPadRight } : undefined}>
                  {isFirst ? (
                    <>
                      {hasCustomHeader ? <LetterHeadCustom /> : <LetterHead />}
                      <RecipientBlock />
                      <MetaRow />
                    </>
                  ) : (
                    <MiniHeader pageNum={pageIdx + 1} totalPages={totalPages} />
                  )}
                  <Seitenkopf />
                  <article style={bodyArticleStyle}>
                    {indices.map(i => renderPara(paragraphs[i], i))}
                  </article>
                  {isLast && (hasCustomFooter ? <FooterCustom /> : <Footer />)}
                </div>
              );
            })}
          </div>
        </FitA4>
      </div>
    </div>
  );
}

const sheetStyles = `
.a4-measure {
  position: absolute;
  left: -10000px;
  top: 0;
  width: 170mm;
  visibility: hidden;
  pointer-events: none;
  font-family: 'Cormorant Garamond', 'Times New Roman', serif;
  font-size: 12pt;
  line-height: 1.6;
  color: #0A192F;
  text-align: justify;
}
.a4-measure p { margin: 0 0 4mm 0; white-space: pre-wrap; }
.a4-measure h3 { font-family: 'Cormorant Garamond', serif; font-size: 14pt; font-weight: 600; margin: 6mm 0 3mm 0; letter-spacing: -0.01em; }
.a4-measure .betreff { font-family: 'IBM Plex Sans', sans-serif; font-weight: 600; font-size: 11pt; margin: 0 0 8mm 0; }

.a4-print-root .a4-page {
  width: 210mm;
  min-height: 297mm;
  padding: 25mm 20mm;
  background: white;
  box-shadow: 0 6px 32px rgba(10, 25, 47, 0.12);
  font-family: 'Cormorant Garamond', 'Times New Roman', serif;
  font-size: 12pt;
  line-height: 1.6;
  color: #0A192F;
  position: relative;
  overflow: hidden;
}
.a4-print-root .ltr-head {
  display: grid; grid-template-columns: 1fr auto;
  align-items: flex-start;
  border-bottom: 1px solid #111; padding-bottom: 6mm; margin-bottom: 8mm;
  font-family: 'IBM Plex Sans', sans-serif;
  gap: 10mm;
}
.a4-print-root .ltr-left { padding-top: 18mm; min-width: 0; }
.a4-print-root .ltr-sender-line {
  font-size: 7pt; color: #4B5563; text-decoration: underline; text-underline-offset: 2px;
}
.a4-print-root .ltr-right { text-align: right; min-width: 65mm; }
.a4-print-root .ltr-logo {
  max-width: 55mm; max-height: 22mm; object-fit: contain; margin-left: auto; margin-bottom: 3mm; display: block;
}
.a4-print-root .ltr-kanzlei { font-weight: 600; font-size: 13pt; color: #0A192F; line-height: 1.2; }
.a4-print-root .ltr-anwalt { font-size: 9pt; color: #0A192F; margin-top: 2mm; }
.a4-print-root .ltr-titel { font-size: 8pt; color: #4B5563; font-style: italic; }
.a4-print-root .ltr-byline { font-size: 9pt; color: #4B5563; margin-top: 1mm; }
.a4-print-root .ltr-minihdr {
  font-family: 'IBM Plex Sans', sans-serif; font-size: 8pt; color: #9CA3AF;
  display: flex; justify-content: space-between; margin-bottom: 8mm;
  padding-bottom: 3mm; border-bottom: 1px solid #F3F4F6;
}
.a4-print-root .rec-block {
  display: grid; grid-template-columns: 1fr auto; gap: 10mm; margin-bottom: 10mm;
  font-family: 'IBM Plex Sans', sans-serif;
}
.a4-print-root .rec-addr { font-size: 10pt; white-space: pre-line; line-height: 1.5; }
.a4-print-root .rec-right { text-align: right; font-size: 9pt; color: #4B5563; line-height: 1.6; white-space: nowrap; }
.a4-print-root .rec-right strong { color: #0A192F; }
.a4-print-root .rec-date { font-weight: 500; }
.a4-print-root .rec-az { font-family: 'IBM Plex Mono', monospace; font-size: 8.5pt; }
.a4-print-root .meta-row { display: none; }
.a4-print-root article { text-align: justify; hyphens: auto; }
.a4-print-root article p { margin: 0 0 4mm 0; white-space: pre-wrap; }
.a4-print-root article h3 {
  font-family: 'Cormorant Garamond', serif; font-size: 14pt; font-weight: 600;
  margin: 6mm 0 3mm 0; letter-spacing: -0.01em;
}
.a4-print-root .betreff {
  font-family: 'IBM Plex Sans', sans-serif; font-weight: 600; font-size: 11pt;
  margin: 0 0 8mm 0; color: #0A192F;
}
.a4-print-root .a4-table, .a4-measure .a4-table {
  width: 100%; border-collapse: collapse; margin: 2mm 0 5mm 0;
  font-family: 'IBM Plex Sans', sans-serif; font-size: 10pt;
}
.a4-print-root .a4-table th, .a4-measure .a4-table th {
  text-align: left; font-weight: 600; font-size: 8.5pt; letter-spacing: 0.04em; text-transform: uppercase;
  color: #6B7280; border-bottom: 1px solid #1A2238; padding: 1.5mm 2mm;
}
.a4-print-root .a4-table td, .a4-measure .a4-table td {
  padding: 1.6mm 2mm; border-bottom: 1px solid #E5E7EB; vertical-align: top;
}
.a4-print-root .a4-table td.num, .a4-print-root .a4-table th:last-child,
.a4-measure .a4-table td.num, .a4-measure .a4-table th:last-child { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
.a4-print-root .a4-table tr.a4-table-total td, .a4-measure .a4-table tr.a4-table-total td {
  font-weight: 600; border-top: 1.5px solid #1A2238; border-bottom: none;
}
.a4-print-root .ltr-foot {
  position: absolute; bottom: 12mm; left: 20mm; right: 20mm;
  padding-top: 4mm; border-top: 1px solid #E5E7EB;
  font-family: 'IBM Plex Sans', sans-serif; font-size: 7.5pt; color: #6B7280;
  display: grid; grid-template-columns: 1fr auto; gap: 8mm; line-height: 1.5;
}
.a4-print-root .ltr-foot .foot-right { text-align: right; }
.a4-print-root mark.hl, .a4-measure mark.hl {
  background: transparent;
  color: inherit;
  padding: 0 1px;
  border-radius: 2px;
}
.a4-print-root mark.hl-money, .a4-measure mark.hl-money {
  background: rgba(211, 47, 47, 0.08);
  color: #7A1F1F;
  font-weight: 500;
  padding: 0 3px;
}
.a4-print-root mark.hl-date, .a4-measure mark.hl-date {
  color: #0A192F;
  font-weight: 500;
}
.a4-print-root mark.hl-law, .a4-measure mark.hl-law {
  font-style: italic;
  color: #1A2238;
  font-weight: 500;
}
.a4-print-root mark.hl-deadline, .a4-measure mark.hl-deadline {
  background: rgba(245, 158, 11, 0.14);
  color: #78350F;
  font-weight: 500;
  padding: 0 3px;
}
.a4-print-root mark.hl-plate, .a4-measure mark.hl-plate {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 0.92em;
  background: rgba(26, 34, 56, 0.05);
  padding: 0 3px;
  letter-spacing: 0.02em;
}
.a4-print-root mark.hl-refnum, .a4-measure mark.hl-refnum {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 0.88em;
  color: #4B5563;
  letter-spacing: 0.01em;
}
.a4-print-root mark.hl-percent, .a4-measure mark.hl-percent {
  color: #1A2238;
  font-weight: 500;
}
`;
